import { applySnapshot, flow, getEnv, types } from 'mobx-state-tree';
import { SortingRule } from 'react-table';
import qs from 'qs';

import { GetItemsQueryParams, MaintainItemsResponse } from 'src/shared/types';
import { HttpClient } from 'src/lib/http-client/http-client';

const Customer = types.model('Customer', {
    id: types.string,
    bringCustomerNumber: types.string,
    firstname: types.optional(types.string, ''),
    name: types.optional(types.string, ''),
    active: types.optional(types.boolean, false),
});

export const BringingCustomersStore = types
    .model('BringingCustomers', {
        page: types.optional(types.number, 0),
        rowsPerPage: types.optional(types.number, 10),
        customers: types.optional(types.array(Customer), []),
        sortField: types.maybeNull(types.string),
        sortDesc: types.maybeNull(types.boolean),
        isFetchDataLoading: types.boolean,
        count: types.number,
    })
    .actions((self) => {
        const {
            env: { httpClient },
        } = getEnv(self);
        return {
            setSorting(sortBy?: SortingRule<string>) {
                if (sortBy) {
                    self.sortField = sortBy.id;
                    self.sortDesc = typeof sortBy.desc === 'boolean' ? sortBy.desc : null;
                } else {
                    self.sortField = null;
                    self.sortDesc = null;
                }
            },
            setPage(page: number) {
                self.page = page;
            },
            setRowsPerPage(rowsPerPage: number) {
                self.rowsPerPage = rowsPerPage;
            },
            fetchItems: flow(function* (params) {
                const [page, rowsPerPage, sortField, sortDesc, searchTerm] =
                    params.queryKey;

                const skip = (page + 1) * rowsPerPage - rowsPerPage;
                const take = rowsPerPage;
                const queryParams: GetItemsQueryParams = {
                    skip,
                    take,
                };
                if (searchTerm) {
                    queryParams.searchTerm = searchTerm;
                }
                if (sortField && typeof sortDesc === 'boolean') {
                    queryParams.sortField =
                        sortField[0].toUpperCase() + sortField.slice(1);
                    queryParams.isAscending = sortDesc;
                }

                try {
                    self.isFetchDataLoading = true;
                    const data = yield (
                        httpClient as HttpClient
                    ).get<MaintainItemsResponse>(
                        `users/bring-customers?${qs.stringify(queryParams)}`
                    );
                    applySnapshot(self, {
                        ...self,
                        count: data.count,
                        customers: data.result,
                    });
                } catch {
                    applySnapshot(self, { ...self, customers: [], count: 0 });
                } finally {
                    self.isFetchDataLoading = false;
                }
            }),
        };
    })
    .views((self) => ({
        get getCustomers() {
            return [...self.customers];
        },
    }));
